import { useListIntegrations } from '@client-app-sdk';
import { IntegrationClientModel } from '@client-app-sdk/api/v1';
import { Combobox, ComboboxOption } from '@common/ui/Combobox/Combobox';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

type IntegrationComboboxProps = {
  value: ComboboxOption | null;
  onChange: (value: ComboboxOption | null) => void;
  disabled?: boolean;
  sessionId?: string;
};

export function IntegrationCombobox({ onChange, value, disabled, sessionId }: IntegrationComboboxProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const { data, hasNextPage, fetchNextPage, isFetching } = useListIntegrations({
    limit: 100,
    search: debouncedSearch,
    sessionId,
  });

  const fetchMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  const integrationComboboxOptions = useMemo(() => {
    return (
      data?.pages.flatMap((page: { data: IntegrationClientModel[] }) =>
        page.data.map((integration) => ({
          value: integration.id,
          label: integration.name,
          subLabel: integration.type,
          icon: integration.icons.svg,
        })),
      ) ?? []
    );
  }, [data]);

  return (
    <Combobox
      value={value}
      label="Integration"
      disabled={disabled}
      loading={isFetching}
      fetchMore={fetchMore}
      onInputChange={setSearch}
      placeholder="Select integration"
      options={integrationComboboxOptions}
      inputValue={search ? search : value?.label}
      onChange={(val) => onChange(Array.isArray(val) ? null : val)}
    />
  );
}
